import { useCallback, useEffect, useRef, useState } from 'react'

import {
  OFFICE_INITIAL_LOAD_FALLBACK_TIMEOUT_MS,
  OFFICE_PDF_FALLBACK_TIMEOUT_MS,
} from '@/components/office/OfficeViewer/config'

import {
  type BrowserPreviewIntent,
  getBrowserPreviewMode,
  isNativeOfficePreviewMode,
} from '@/helpers/file'

import type { File } from '@/types/File'

interface UseOfficePdfFallbackOptions {
  previewIntent?: BrowserPreviewIntent
}

interface NativePreviewRevisionToken {
  key: string | null
}

interface NativePreviewAttemptToken {
  revision: NativePreviewRevisionToken
}

export function useOfficePdfFallback(
  file: File | null | undefined,
  options?: UseOfficePdfFallbackOptions,
) {
  const revisionKey = file
    ? `${file.id}:${file.last_modified}:${file.size}`
    : null
  const revisionTokenRef = useRef<NativePreviewRevisionToken>({
    key: revisionKey,
  })
  if (revisionTokenRef.current.key !== revisionKey) {
    revisionTokenRef.current = { key: revisionKey }
  }
  const revisionToken = revisionTokenRef.current
  const previewMode = file
    ? getBrowserPreviewMode(file, { intent: options?.previewIntent })
    : 'download'
  const isNativePreview = isNativeOfficePreviewMode(previewMode)
  // Static previews (html/markdown/text/image) render in one shot with no
  // paged fallback of their own.
  const isStaticPreview =
    previewMode === 'html' ||
    previewMode === 'markdown' ||
    previewMode === 'text' ||
    previewMode === 'image'
  const nativeAttemptRef = useRef<NativePreviewAttemptToken | null>(null)
  if (!isNativePreview && !isStaticPreview) {
    nativeAttemptRef.current = null
  } else if (nativeAttemptRef.current?.revision !== revisionToken) {
    nativeAttemptRef.current = { revision: revisionToken }
  }
  const nativeAttempt = nativeAttemptRef.current
  const [nativeRenderStartedAttempt, setNativeRenderStartedAttempt] =
    useState<NativePreviewAttemptToken | null>(null)
  const [nativePreviewLoadedAttempt, setNativePreviewLoadedAttempt] =
    useState<NativePreviewAttemptToken | null>(null)
  const [forcePdfFallbackAttempt, setForcePdfFallbackAttempt] =
    useState<NativePreviewAttemptToken | null>(null)
  const nativeRenderStarted = nativeRenderStartedAttempt === nativeAttempt
  const nativePreviewLoaded = nativePreviewLoadedAttempt === nativeAttempt
  const forcePdfFallback = forcePdfFallbackAttempt === nativeAttempt
  const effectivePreviewMode =
    (isNativePreview || isStaticPreview) && forcePdfFallback && file?.pdf_url
      ? 'pdf'
      : previewMode

  // The native office pipeline is rAF/CPU-throttled to a standstill in hidden
  // tabs while setTimeout still fires, so an armed timer would latch the PDF
  // fallback for any sheet/deck that wasn't fully loaded when the tab was
  // backgrounded — and the latch only resets on a revision change, so the
  // user would come back to the PDF renderer. Timers therefore count visible
  // time only: cleared when the tab hides, re-armed for the full duration
  // when it returns. Genuine load errors still latch via
  // handleNativeLoadError regardless of visibility.
  const [isPageVisible, setIsPageVisible] = useState<boolean>(
    () =>
      typeof document === 'undefined' || document.visibilityState !== 'hidden',
  )

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsPageVisible(document.visibilityState !== 'hidden')
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [])

  useEffect(() => {
    if (
      !isNativePreview ||
      !file?.pdf_url ||
      !isPageVisible ||
      nativeRenderStarted ||
      nativePreviewLoaded ||
      forcePdfFallback
    ) {
      return
    }

    const timeoutId = setTimeout(() => {
      setForcePdfFallbackAttempt(nativeAttempt)
    }, OFFICE_INITIAL_LOAD_FALLBACK_TIMEOUT_MS)

    return () => clearTimeout(timeoutId)
  }, [
    file?.pdf_url,
    forcePdfFallback,
    isPageVisible,
    isNativePreview,
    nativeAttempt,
    nativePreviewLoaded,
    nativeRenderStarted,
  ])

  useEffect(() => {
    if (
      !isNativePreview ||
      !file?.pdf_url ||
      !isPageVisible ||
      !nativeRenderStarted ||
      nativePreviewLoaded ||
      forcePdfFallback
    ) {
      return
    }

    const timeoutId = setTimeout(() => {
      setForcePdfFallbackAttempt(nativeAttempt)
    }, OFFICE_PDF_FALLBACK_TIMEOUT_MS)

    return () => clearTimeout(timeoutId)
  }, [
    file?.pdf_url,
    forcePdfFallback,
    isPageVisible,
    nativePreviewLoaded,
    nativeRenderStarted,
    isNativePreview,
    nativeAttempt,
  ])

  const handleNativeRenderStart = useCallback(() => {
    if (
      !nativeAttempt?.revision.key ||
      nativeAttemptRef.current !== nativeAttempt
    ) {
      return
    }
    setNativeRenderStartedAttempt(nativeAttempt)
  }, [nativeAttempt])

  const handleNativeLoadSuccess = useCallback(() => {
    if (
      !nativeAttempt?.revision.key ||
      nativeAttemptRef.current !== nativeAttempt
    ) {
      return
    }
    setNativePreviewLoadedAttempt(nativeAttempt)
  }, [nativeAttempt])

  const handleNativeLoadError = useCallback(() => {
    if (
      nativeAttempt?.revision.key &&
      nativeAttemptRef.current === nativeAttempt &&
      file?.pdf_url
    ) {
      setForcePdfFallbackAttempt(nativeAttempt)
    }
  }, [file?.pdf_url, nativeAttempt])

  return {
    previewMode,
    effectivePreviewMode,
    handleNativeRenderStart,
    handleNativeLoadSuccess,
    handleNativeLoadError,
    // Static previews (html/markdown/image) share the same guarded error
    // path: any load failure falls back to the PDF rendition when one exists.
    handleStaticPreviewLoadError: handleNativeLoadError,
  }
}
